import React from 'react';
import { Link } from 'react-router-dom';
import { Product } from '@/types';
import { useCart } from '@/context/CartContext';
import { useLanguage } from '@/context/LanguageContext';
import { Card, Image, Text, Button, Group, Badge, Box } from '@mantine/core';

interface ProductCardProps {
  product: Product;
}

export const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const { addItem, getProductQuantity } = useCart();
  const { t } = useLanguage();
  const cartQuantity = getProductQuantity(product.id);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    addItem(product, 1);
  };

  return (
    <Link to={`/product/${product.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
      <Card
        shadow="sm"
        padding="lg"
        radius="md"
        withBorder
        style={{ cursor: 'pointer', height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <Card.Section style={{ position: 'relative' }}>
          {product.images && product.images.length > 0 ? (
            <>
              <Image
                src={product.images[0]}
                height={200}
                alt={product.name}
                fit="cover"
                style={!product.inStock ? { opacity: 0.6 } : {}}
              />
              {!product.inStock && (
                <Badge
                  color="red"
                  variant="filled"
                  size="lg"
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                  }}
                >
                  {t('product.outOfStock')}
                </Badge>
              )}
            </>
          ) : (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5' }}>
              <Text c="dimmed">{t('product.noImage')}</Text>
            </div>
          )}
        </Card.Section>

        <Group justify="space-between" mt="md" mb="xs">
          <Text fw={600} size="lg" lineClamp={2}>
            {product.name}
          </Text>
        </Group>

        <Box mb="md">
          <Text size="xl" fw={700}>
            {product.price.toFixed(2)} {t('common.pln')} <Text component="span" size="xs" fw={500} c="dimmed">{t('product.gross') || 'brutto'}</Text>
          </Text>
          <Text size="xs" c="dimmed">
            {(product.price / 1.23).toFixed(2)} {t('common.pln')} {t('product.net') || 'netto'}
          </Text>
        </Box>

        {product.inStock ? (
          <Group gap="xs" mt="auto">
            <Button
              variant="filled"
              onClick={handleAddToCart}
              style={{ flex: 1 }}
            >
              {t('product.addToCart')}
            </Button>
            {cartQuantity > 0 && (
              <Badge color="dark" size="lg" variant="filled" style={{ minWidth: '60px' }}>
                {cartQuantity} {t('product.inCart')}
              </Badge>
            )}
          </Group>
        ) : (
          <Badge color="red" variant="light" fullWidth py="md">
            {t('product.outOfStock')}
          </Badge>
        )}
      </Card>
    </Link>
  );
};
